import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { ArrowLeft, Braces, Code2, Eye, FilePlus2, GitBranch, History, Languages, LayoutPanelLeft, Redo2, Rocket, Save, Settings2, Trash2, Undo2, XCircle } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { countFields, lintDocument, type FormLocale } from "@ncp/forms";
import { Badge, Button, Dialog, MOD_PREFIX, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, InlineAlert, Skeleton, Tabs, TabsList, TabsTrigger, Textarea, Tooltip, cn, notifySaved, toast } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { UnsavedChangesDialog, useUnsavedChanges } from "../lib/useUnsavedChanges";
import { bodyOf, useFormActions, useFormDefinition, type FormBody, type FormDefinition, type FormLint } from "../features/forms/api";
import { stableJson, useHistory, useUndoShortcuts, type Selection } from "../features/forms/model";
import { DesignCanvas } from "../features/forms/DesignCanvas";
import { RulesPanel } from "../features/forms/RulesPanel";
import { FormPreview } from "../features/forms/FormPreview";
import { CodePanel } from "../features/forms/CodePanel";
import { FormSettings } from "../features/forms/FormSettings";
import { VersionsPanel } from "../features/forms/VersionsPanel";

type Tab = "design" | "rules" | "preview" | "code" | "settings" | "versions";
const TABS: Array<{ key: Tab; icon: typeof Braces }> = [
  { key: "design", icon: LayoutPanelLeft }, { key: "rules", icon: GitBranch }, { key: "preview", icon: Eye }, { key: "code", icon: Code2 }, { key: "settings", icon: Settings2 }, { key: "versions", icon: History },
];
const STATUS_VARIANT: Record<string, "success" | "warning" | "info" | "default"> = { Published: "success", InReview: "warning", Draft: "info", Retired: "default" };

/** Form Builder: three-pane designer, rule builder, live RTL preview, CodeMirror view, settings and version diff for one FormDefinition. */
export function FormBuilderPage() {
  const { key = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const version = params.get("version") ? Number(params.get("version")) : undefined;
  const tab = (params.get("tab") as Tab | null) ?? "design";
  const setTab = (next: Tab) => setParams((p) => { p.set("tab", next); return p; }, { replace: true });
  const definition = useFormDefinition(key, version);

  if (definition.isLoading) return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-[60vh]" /></div>;
  if (definition.isError || !definition.data) return <FormMissing formKey={key} />;
  return <Builder key={definition.data.id} definition={definition.data} tab={tab} onTab={setTab} onOpenVersion={(v) => setParams((p) => { p.set("version", String(v)); return p; })} />;
}

function FormMissing({ formKey }: { formKey: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBreadcrumbs([{ label: t("admin:nav.forms"), href: "/forms" }, { label: formKey }]);
  return <EmptyState icon={XCircle} title={t("admin:forms.builder.notFound")} description={t("admin:forms.builder.notFoundBody", { key: formKey })} action={<Button variant="outline" onClick={() => navigate("/forms")}><ArrowLeft /> {t("admin:common.back")}</Button>} />;
}

function Builder({ definition, tab, onTab, onOpenVersion }: { definition: FormDefinition; tab: Tab; onTab: (t: Tab) => void; onOpenVersion: (v: number) => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canWrite = can("platform.forms.write");
  const canPublish = can("platform.releases.write");
  const actions = useFormActions();

  const history = useHistory<FormBody>(bodyOf(definition));
  useUndoShortcuts(history);
  const body = history.present;
  const savedJson = useRef(stableJson(bodyOf(definition)));
  const dirty = stableJson(body) !== savedJson.current;
  const blocker = useUnsavedChanges(dirty);

  const editable = canWrite && definition.status === "Draft";
  const [selection, setSelection] = useState<Selection>(null);
  const [activeStep, setActiveStep] = useState<string | null>(body.uiSchema.steps[0]?.key ?? null);
  const [designLocale, setDesignLocale] = useState<FormLocale>((i18n.language === "ar" ? "ar" : "en") as FormLocale);
  const [serverLint, setServerLint] = useState<FormLint | null>(null);
  const [confirm, setConfirm] = useState<"discard" | "stage" | "newVersion" | null>(null);
  const [note, setNote] = useState("");

  const lint = useMemo(() => lintDocument(body.schema, body.uiSchema, body.rules), [body]);
  const fields = useMemo(() => countFields(body.schema), [body.schema]);

  useEffect(() => { if (!body.uiSchema.steps.some((s) => s.key === activeStep)) setActiveStep(body.uiSchema.steps[0]?.key ?? null); }, [body.uiSchema.steps, activeStep]);

  const title = body.name || definition.key;
  useBreadcrumbs(useMemo(() => [{ label: t("admin:nav.forms"), href: "/forms" }, { label: title }], [t, title]));
  useHelpTopic({ title: t("admin:forms.builder.helpTitle"), docsAnchor: "form-builder", body: (<><p>{t("admin:forms.builder.help.design")}</p><p>{t("admin:forms.builder.help.rules")}</p><p>{t("admin:forms.builder.help.publish")}</p><p>{t("admin:forms.builder.help.shortcuts", { mod: MOD_PREFIX })}</p></>) });

  /** Saves the draft; whether it is stored (a failure was reported by useSave). */
  const save = useCallback(async (): Promise<boolean> => {
    if (!editable || !dirty) return true;
    if (lint.errors.length) { toast.error(t("admin:forms.builder.fixErrors", { count: lint.errors.length })); onTab("code"); return false; }
    try {
      const saved = await actions.update.mutateAsync({ id: definition.id, body, changeNote: note || undefined });
      savedJson.current = stableJson(bodyOf(saved));
      history.reset(bodyOf(saved));
      setNote("");
      notifySaved(saved, t("admin:common.saved"));
      return true;
    } catch { return false; /* useSave reported it */ }
  }, [actions.update, body, definition.id, dirty, editable, history, lint.errors.length, note, onTab, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void save(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  usePaletteActions(useMemo(() => [
    ...(editable ? [{ id: "forms.save", label: t("admin:forms.builder.save"), icon: Save, permission: "platform.forms.write", run: () => void save() }] : []),
    { id: "forms.preview", label: t("admin:forms.tabs.preview"), icon: Eye, permission: "platform.forms.read", run: () => onTab("preview") },
    { id: "forms.code", label: t("admin:forms.tabs.code"), icon: Code2, permission: "platform.forms.read", run: () => onTab("code") },
  ], [editable, onTab, save, t]));

  const stage = async () => {
    // An unsaved draft that could not be stored is not staged: the stored version is not what the screen shows.
    if (dirty && !(await save())) return;
    try {
      const cs = await actions.stage.mutateAsync({ id: definition.id, titleHint: t("admin:forms.builder.stageTitle", { name: title }) });
      notifySaved(cs, t("admin:forms.builder.staged"));
      setConfirm(null);
      navigate(`/releases/${cs.id}`);
    } catch { /* useSave reported it */ }
  };
  const newVersion = async () => {
    try {
      const d = await actions.newVersion.mutateAsync({ key: definition.key, changeNote: note || undefined });
      setConfirm(null); setNote("");
      notifySaved(d, t("admin:forms.builder.versionCreated", { version: d.version }));
      onOpenVersion(d.version);
    } catch { /* useSave reported it */ }
  };
  const discard = async () => {
    try { notifySaved(await actions.discard.mutateAsync(definition.id), t("admin:forms.builder.discarded")); navigate("/forms"); } catch { /* useSave reported it */ }
  };
  const withdraw = async () => {
    try { notifySaved(await actions.withdraw.mutateAsync(definition.id), t("admin:forms.builder.withdrawn")); } catch { /* useSave reported it */ }
  };
  const runServerLint = async () => {
    try { setServerLint(await actions.lint.mutateAsync({ schema: body.schema, uiSchema: body.uiSchema, rules: body.rules })); } catch { /* useSave reported it */ }
  };

  const onChange = useCallback((next: FormBody | ((prev: FormBody) => FormBody), opts?: { transient?: boolean }) => { if (editable) history.set(next, opts); }, [editable, history]);

  return (
    <div className="-mt-2">
      <UnsavedChangesDialog blocker={blocker} />
      {/* Header */}
      <div className="sticky top-14 z-20 -mx-4 mb-3 border-b border-sand-200 bg-background/90 px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/forms")}><ArrowLeft className="rtl:rotate-180" /></Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <h1 className="truncate text-base font-semibold">{title}</h1>
              <Badge variant="outline" size="sm" className="font-mono">{definition.key}</Badge>
              <Badge variant="default" size="sm">v{definition.version}</Badge>
              <Badge variant={STATUS_VARIANT[definition.status] ?? "default"} size="sm" dot>{t(`admin:forms.status.${definition.status}`)}</Badge>
              {dirty ? <Badge variant="warning" size="sm">{t("admin:common.unsaved")}</Badge> : null}
              {lint.errors.length ? <Tooltip content={lint.errors[0]}><Badge variant="danger" size="sm">{t("admin:common.issues", { count: lint.errors.length })}</Badge></Tooltip> : lint.warnings.length ? <Tooltip content={lint.warnings[0]}><Badge variant="warning" size="sm">{t("admin:forms.builder.warnings", { count: lint.warnings.length })}</Badge></Tooltip> : null}
            </div>
            <p className="text-xs text-muted-foreground">{t("admin:forms.builder.summary", { fields, steps: body.uiSchema.steps.length, rules: body.rules.length })}</p>
          </div>
          <div className="ms-auto flex flex-wrap items-center gap-1">
            {tab === "design" || tab === "rules" ? <Button size="sm" variant="ghost" onClick={() => setDesignLocale((l) => (l === "en" ? "ar" : "en"))}><Languages /> {designLocale === "en" ? "EN" : "AR"}</Button> : null}
            {editable ? (<>
              <Tooltip content={`${t("admin:forms.builder.undo")} (${MOD_PREFIX}Z)`}><Button size="sm" variant="ghost" disabled={!history.canUndo} onClick={history.undo} aria-label={t("admin:forms.builder.undo")}><Undo2 /></Button></Tooltip>
              <Tooltip content={`${t("admin:forms.builder.redo")} (⇧${MOD_PREFIX}Z)`}><Button size="sm" variant="ghost" disabled={!history.canRedo} onClick={history.redo} aria-label={t("admin:forms.builder.redo")}><Redo2 /></Button></Tooltip>
              <Button size="sm" variant="ghost" className="text-danger-600" onClick={() => setConfirm("discard")}><Trash2 /> {t("admin:forms.builder.discard")}</Button>
              <Button size="sm" variant={dirty ? "gold" : "outline"} loading={actions.update.isPending} disabled={!dirty} onClick={() => void save()}><Save /> {t("admin:forms.builder.save")}</Button>
              {canPublish ? <Button size="sm" variant="default" disabled={lint.errors.length > 0 || fields === 0} onClick={() => setConfirm("stage")}><Rocket /> {t("admin:forms.builder.stage")}</Button> : null}
            </>) : null}
            {canWrite && definition.status === "InReview" ? <Button size="sm" variant="outline" onClick={withdraw} loading={actions.withdraw.isPending}>{t("admin:forms.builder.withdraw")}</Button> : null}
            {canWrite && (definition.status === "Published" || definition.status === "Retired") ? <Button size="sm" variant="gold" onClick={() => setConfirm("newVersion")}><FilePlus2 /> {t("admin:forms.builder.newVersion")}</Button> : null}
          </div>
        </div>
        <Tabs value={tab} onValueChange={(v) => onTab(v as Tab)} className="mt-2">
          <TabsList>{TABS.map(({ key, icon: Icon }) => <TabsTrigger key={key} value={key}><Icon className="size-3.5" /> {t(`admin:forms.tabs.${key}`)}{key === "rules" && body.rules.length ? <Badge variant="outline" size="sm" className="ms-1">{body.rules.length}</Badge> : null}</TabsTrigger>)}</TabsList>
        </Tabs>
      </div>

      {!editable && definition.status === "Draft" && !canWrite ? <InlineAlert tone="info" className="mb-3">{t("admin:common.permissionDenied")}</InlineAlert> : null}
      {definition.status === "InReview" ? <InlineAlert tone="warning" className="mb-3">{t("admin:forms.builder.inReviewNotice")}</InlineAlert> : null}
      {definition.status === "Published" ? <InlineAlert tone="info" className="mb-3">{t("admin:forms.builder.publishedNotice")}</InlineAlert> : null}

      <div className={cn(tab === "design" ? "" : "mx-auto max-w-[1400px]")}>
        {tab === "design" ? <DesignCanvas body={body} onChange={onChange} selection={selection} onSelect={setSelection} locale={designLocale} readOnly={!editable} activeStep={activeStep} onActiveStepChange={setActiveStep} /> : null}
        {tab === "rules" ? <RulesPanel body={body} onChange={onChange} locale={designLocale} readOnly={!editable} /> : null}
        {tab === "preview" ? <FormPreview body={body} formKey={definition.key} formId={definition.id} onSampleDataChange={editable ? (d) => onChange({ ...body, sampleData: d }) : undefined} validateOnServer={(data, locale) => actions.validate.mutateAsync({ id: definition.id, data, locale })} /> : null}
        {tab === "code" ? <CodePanel body={body} onChange={(b) => onChange(b)} readOnly={!editable} serverLint={serverLint} onServerLint={runServerLint} linting={actions.lint.isPending} /> : null}
        {tab === "settings" ? <FormSettings body={body} formKey={definition.key} onChange={onChange} readOnly={!editable} /> : null}
        {tab === "versions" ? <VersionsPanel formKey={definition.key} currentVersion={definition.version} onOpenVersion={onOpenVersion} /> : null}
      </div>

      <Dialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{confirm === "discard" ? t("admin:forms.builder.discardTitle") : confirm === "stage" ? t("admin:forms.builder.stageDialogTitle") : t("admin:forms.builder.newVersionTitle")}</DialogTitle>
            <DialogDescription>{confirm === "discard" ? t("admin:forms.builder.discardBody") : confirm === "stage" ? t("admin:forms.builder.stageBody") : t("admin:forms.builder.newVersionBody", { version: definition.version + 1 })}</DialogDescription>
          </DialogHeader>
          {confirm !== "discard" ? <Field label={t("admin:common.changeNote")} hint={t("admin:common.changeNoteHint")}><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[72px]" /></Field> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>{t("admin:common.cancel")}</Button>
            {confirm === "discard" ? <Button variant="destructive" loading={actions.discard.isPending} onClick={discard}>{t("admin:forms.builder.discard")}</Button> : null}
            {confirm === "stage" ? <Button variant="default" loading={actions.stage.isPending || actions.update.isPending} onClick={stage}><Rocket /> {t("admin:forms.builder.stage")}</Button> : null}
            {confirm === "newVersion" ? <Button variant="gold" loading={actions.newVersion.isPending} onClick={newVersion}><FilePlus2 /> {t("admin:forms.builder.newVersion")}</Button> : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
